import { Spacing4, Spacing8, Spacing16 } from 'autogen/design-tokens/tokens';
import { Typography } from 'components/generic/typography';
import Flex from 'components/Layout/components/Flex';
import { customScrollbar } from 'modules/helpers/mixins';
import React, { type ComponentPropsWithoutRef, forwardRef, useId, useState } from 'react';
import styled, { css } from 'styled-components';
import HelperText from '../components/HelperText';
import type { BaseInputProps } from '../shared/types';
import { InvalidInputFocusStyle, InvalidInputStyle, ValidInputDisabledStyle, ValidInputStyle } from '../theme';

const LabelContainer = styled.label`
       display: inline-flex;
       flex-direction: column;
       gap: ${Spacing4};
       margin-bottom: 0;
       min-width: 0;
`;

const StyledTextArea = styled.textarea<{ $isInvalid?: boolean }>`
    ${ValidInputStyle}
    min-height: 96px;
    border: ${({ theme }) => `1px solid ${theme.ColorBorderPrimary};`};
    border-radius: ${({ theme }) => theme.RadiusMedium};
    padding: ${Spacing8} ${Spacing16};

    ${customScrollbar};

    &:hover {
        border-color: ${({ theme }) => theme.ColorBorderHover};
    }

    
    &:focus {
        border-color: ${({ theme }) => theme.ColorBorderFocus};
        outline-offset: 0;
        outline-width: 1px;
    }


    &:disabled {
        ${ValidInputDisabledStyle}
    }

    &::placeholder {
        color: ${({ theme }) => theme.ColorTextDisabled};
    }

    ${({ $isInvalid }) =>
        $isInvalid &&
        css`
            &&,
            &&:hover {
                ${InvalidInputStyle}
            }

            &&:focus {
                ${InvalidInputFocusStyle}
            }
        `}
`;

const WordCount = styled(Typography.Body)`
    margin-left: auto;
    color: ${({ theme }) => theme.ColorTextSecondary};
`;

export type ControlledTextArea = ComponentPropsWithoutRef<'textarea'> & BaseInputProps;

const BaseTextArea = React.forwardRef<HTMLTextAreaElement, ControlledTextArea>((props, ref) => {
    const { className, isInvalid, label, helperText, maxLength, onChange, ...rest } = props;

    return (
        <StyledTextArea
            ref={ref}
            className={className}
            $isInvalid={isInvalid}
            onChange={(e) => {
                onChange?.(e);
            }}
            maxLength={maxLength}
            {...rest}
        />
    );
});

const TextArea = forwardRef<HTMLTextAreaElement, ControlledTextArea>((props, ref) => {
    const { className, isInvalid, label, helperText, maxLength, onChange, ...rest } = props;
    const helperTextId = useId();
    const [wordCount, setWordCount] = useState(() => {
        if (typeof props.value === 'string') return props.value.length;
        if (typeof props.value === 'number') return props.value.toString().length;
        return 0;
    });

    if (label || helperText || maxLength) {
        return (
            <LabelContainer className={className}>
                {label && (
                    <Typography.Label size="S" strong>
                        {label}
                    </Typography.Label>
                )}

                {helperText || maxLength ? (
                    <Flex.Column gap={Spacing4}>
                        <BaseTextArea
                            ref={ref}
                            isInvalid={isInvalid}
                            onChange={(e) => {
                                setWordCount(e.target.value.length);
                                onChange?.(e);
                            }}
                            maxLength={maxLength}
                            aria-describedby={helperTextId}
                            {...rest}
                        />
                        <Flex.Row>
                            {helperText && <HelperText helperText={helperText} helperTextId={helperTextId} />}
                            {maxLength && <WordCount size="S">{`${wordCount}/${maxLength}`}</WordCount>}
                        </Flex.Row>
                    </Flex.Column>
                ) : (
                    <BaseTextArea
                        ref={ref}
                        isInvalid={isInvalid}
                        onChange={(e) => {
                            setWordCount(e.target.value.length);
                            onChange?.(e);
                        }}
                        maxLength={maxLength}
                        {...rest}
                    />
                )}
            </LabelContainer>
        );
    }

    return (
        <BaseTextArea
            ref={ref}
            isInvalid={isInvalid}
            onChange={(e) => {
                setWordCount(e.target.value.length);
                onChange?.(e);
            }}
            maxLength={maxLength}
            className={className}
            {...rest}
        />
    );
});

export default TextArea;
